'use client';

import type { MouseEvent as ReactMouseEvent, ReactNode, SyntheticEvent } from 'react';
import { useSyncExternalStore } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import { ArrowUpCircle } from 'lucide-react';
import { EXTERNAL_LINKS } from '@enter/business-components/lib/external-links';
import { createTrackingLink, UTM_CONTENT } from '@enter/business-components/lib/url';
import { LanguageSwitcher } from '@/components/language-switcher';
import { useCookieConsent } from '@/contexts/consent';
import { EnterDoorLogoMark } from '@/components/logo';
import { useMixpanel } from '@/contexts/mixpanel';
import { useLocalizedNavigation } from '@/contexts/i18n/navigation';
import {
    ENTER_HOME_PRICING_PATH,
    buildEnterHomePricingNavigationTrack,
    buildEnterHomePricingUrl,
    openEnterHomePricingWindow,
} from '@/lib/enter-home-pricing-route';
import { LEGAL_LINKS } from '@/lib/legal-links';
import { FOOTER_PRODUCT_LINKS } from '@/components/site-footer/footer-links';
import '@/components/site-footer/index.css';

type LandingV2ActionType = 'navigate' | 'open_modal' | 'scroll' | 'select';
type LandingButtonTrackingAttributes = Record<`data-landing-${string}`, string>;

function subscribeLocationChange(listener: () => void) {
    window.addEventListener('popstate', listener);
    window.addEventListener('hashchange', listener);
    return () => { window.removeEventListener('popstate', listener); window.removeEventListener('hashchange', listener); };
}
const currentUrl = () => window.location.href;
const serverUrl = () => undefined;

export interface FooterProps {
    /** 首页使用统一 Landing 事件；其它页面保留原事件。 */
    trackingSurface?: 'landing_v2';
    /** 页面提供当前会话上下文；Footer 自己确定事件和参数。 */
    getTrackingContext?: () => Record<string, unknown> | null;
}

function FooterLinkColumn({ title, children }: { title: string; children: ReactNode }) {
    return (
        <section className="link-footer__column flex min-w-0 w-full flex-col items-start gap-[27px] pr-4">
            <h3 title={title} className="m-0 max-w-full truncate text-[11px] font-medium leading-[16.5px] tracking-[0.88px] text-[#a8a29e] uppercase">{title}</h3>
            <nav
                aria-label={title}
                className="flex w-full min-w-0 flex-col items-start gap-[19px] text-[13px] font-normal leading-[19.5px] *:cursor-pointer *:border-0 *:bg-transparent *:p-0 *:text-left *:text-inherit *:no-underline *:max-w-full *:truncate *:[font:inherit] *:transition-opacity *:duration-180 *:ease-[ease] [&>:focus-visible]:opacity-55 [@media(hover:hover)_and_(pointer:fine)]:[&>:hover]:opacity-55"
            >{children}</nav>
        </section>
    );
}

export function Footer({ trackingSurface, getTrackingContext }: FooterProps = {}) {
    const t = useTranslations('footer');
    const locale = useLocale();
    const localizedNavigation = useLocalizedNavigation();
    const { logger } = useMixpanel();
    const { openPreferenceCenter } = useCookieConsent();
    const returnToUrl = useSyncExternalStore(subscribeLocationChange, currentUrl, serverUrl);
    const isLandingV2 = trackingSurface === 'landing_v2';

    const handleActionCapture = (event: SyntheticEvent) => {
        if (!isLandingV2 || !(event.target instanceof Element)) return;
        const element = event.target.closest<HTMLElement>('[data-landing-button-id]');
        if (!element || (event.type === 'click' && element.tagName === 'SELECT')) return;
        const { landingButtonId, landingButtonScreen, landingAction, landingDestination, landingContentKey } = element.dataset;
        if (!landingButtonId || !landingButtonScreen || !landingAction) return;
        const common = getTrackingContext?.();
        if (!common) return;
        const contentKey = event.type === 'change' && event.target instanceof HTMLSelectElement ? event.target.value : landingContentKey;
        logger('home_landing_button_click', {
            ...common, button_id: landingButtonId, screen_id: landingButtonScreen, action_type: landingAction,
            ...(landingDestination ? { destination: landingDestination } : {}),
            ...(contentKey ? { content_key: contentKey } : {}),
        });
    };

    const trackLegacyFooterClick = (slug: string) => {
        if (!isLandingV2) logger('home_footer_item_click', { slug });
    };

    const landingButtonAttrs = (
        buttonId: string,
        actionType: LandingV2ActionType = 'navigate',
        destination?: string,
    ): Partial<LandingButtonTrackingAttributes> =>
        isLandingV2
            ? {
                  'data-landing-button-id': buttonId,
                  'data-landing-button-screen': 'footer',
                  'data-landing-action': actionType,
                  ...(destination
                      ? {
                            'data-landing-destination': destination,
                            'data-landing-content-key': destination,
                        }
                      : {}),
              }
            : {};

    const handlePricing = (event: ReactMouseEvent<HTMLAnchorElement>) => {
        event.preventDefault();
        const businessTraceId = crypto.randomUUID();
        if (!isLandingV2) logger('footer_pricing_click', { source: 'footer' });
        const result = openEnterHomePricingWindow(buildEnterHomePricingUrl(
            ENTER_HOME_PRICING_PATH,
            undefined,
            { businessTraceId, entry: 'footer.pricing', locale, returnTo: window.location.href },
        ));
        const track = buildEnterHomePricingNavigationTrack({
            businessTraceId,
            entry: 'footer.pricing',
            result,
        });
        logger(track.eventName, track.props, track.options);
    };

    const navigate = (label: string, path: string, slug: string) => (
        <button
            title={label}
            type="button"
            onClick={() => {
                trackLegacyFooterClick(slug);
                localizedNavigation.push(path);
            }}
            {...landingButtonAttrs(`footer.${slug}`, 'navigate', slug)}
        >
            {label}
        </button>
    );

    const pricingUrl = buildEnterHomePricingUrl(ENTER_HOME_PRICING_PATH, undefined, {
        entry: 'footer.pricing',
        locale,
        returnTo: returnToUrl,
    });

    return (
        <footer
            className="link-footer relative min-h-[648px] bg-[#f8f8f6] px-10 pt-[60px] pb-10 text-[#1c1917] [font-family:'Instrument_Sans','Inter',sans-serif] [@media(width<=900px)]:px-5 [@media(width<=900px)]:pt-12 [@media(width<=900px)]:pb-8"
            data-landing-tracking-owner="component"
            onClickCapture={handleActionCapture}
            onChangeCapture={handleActionCapture}
            id="resources"
            aria-label={t('ariaLabels.siteFooter')}
            data-landing-screen={isLandingV2 ? 'footer' : undefined}
        >
            <div className="link-footer__container mx-auto flex min-h-[548px] w-full max-w-[1680px] flex-col justify-between [@media(901px<=width<=1200px)]:gap-20 [@media(width<=900px)]:min-h-0 [@media(width<=900px)]:gap-[72px]">
                <div className="link-footer__top flex items-start justify-between gap-10 [@media(901px<=width<=1200px)]:flex-col [@media(901px<=width<=1200px)]:gap-12 [@media(width<=900px)]:flex-col [@media(width<=900px)]:gap-12">
                    <button
                        type="button"
                        className="link-footer__brand flex h-8 w-[186.66px] flex-none cursor-pointer items-center border-0 bg-transparent px-0 pt-0.5 pb-0 text-inherit"
                        aria-label={t('ariaLabels.enterHome')}
                        onClick={() => localizedNavigation.push('/')}
                        {...landingButtonAttrs('footer.brand', 'navigate', 'home')}
                    >
                        <span className="footer-brand-logo flex items-center gap-[9px]" aria-hidden="true">
                            <span className="footer-brand-logo__mark grid h-7 w-[23px] flex-none place-items-center">
                                <EnterDoorLogoMark
                                    className="footer-brand-logo__shared-mark size-full [&_.enter-home-door-logo-shape]:size-full"
                                    testId="enter-home-footer-door-logo"
                                />
                            </span>
                            <span className="footer-brand-logo__word relative block h-[17px] w-[63.2px] [&>img]:absolute [&>img]:top-0 [&>img]:block [&>img]:max-w-none [&>img:nth-child(1)]:left-0 [&>img:nth-child(1)]:w-[13.05px] [&>img:nth-child(2)]:left-[14.86px] [&>img:nth-child(2)]:top-[5.88px] [&>img:nth-child(2)]:w-[13.52px] [&>img:nth-child(3)]:left-[30.17px] [&>img:nth-child(3)]:top-[2.1px] [&>img:nth-child(3)]:w-[7.2px] [&>img:nth-child(4)]:left-[39.18px] [&>img:nth-child(4)]:top-[5.72px] [&>img:nth-child(4)]:w-[14.27px] [&>img:nth-child(5)]:left-[55.24px] [&>img:nth-child(5)]:top-[6.03px] [&>img:nth-child(5)]:w-[7.96px]">
                                <img src="/_enter_home/landing-v2/logo-union.svg" alt="" />
                                <img src="/_enter_home/landing-v2/logo-group-1.svg" alt="" />
                                <img src="/_enter_home/landing-v2/logo-vector.svg" alt="" />
                                <img src="/_enter_home/landing-v2/logo-group.svg" alt="" />
                                <img src="/_enter_home/landing-v2/logo-vector-1.svg" alt="" />
                            </span>
                        </span>
                    </button>

                    <div className="link-footer__navigation grid min-w-0 w-[933.35px] max-w-full shrink grid-cols-5 [@media(901px<=width<=1200px)]:w-full [@media(901px<=width<=1200px)]:grid-cols-5 [@media(width<=900px)]:w-full [@media(width<=900px)]:grid-cols-2 [@media(width<=900px)]:gap-x-6 [@media(width<=900px)]:gap-y-12">
                        <FooterLinkColumn title={t('products.title')}>
                            {FOOTER_PRODUCT_LINKS.map((link) => link.type === 'internal' ? (
                                <button
                                    title={t(link.labelKey)}
                                    type="button"
                                    key={link.slug}
                                    onClick={() => {
                                        trackLegacyFooterClick(link.slug);
                                        localizedNavigation.push(link.path);
                                    }}
                                    {...landingButtonAttrs(`footer.product.${link.slug}`, 'navigate', link.slug)}
                                >
                                    {t(link.labelKey)}
                                </button>
                            ) : (
                                <a
                                    title={t(link.labelKey)}
                                    key={link.slug}
                                    href={createTrackingLink(link.href, UTM_CONTENT.FOOTER_PRODUCTS)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={() => trackLegacyFooterClick(link.slug)}
                                    {...landingButtonAttrs(`footer.product.${link.slug}`, 'navigate', link.slug)}
                                >
                                    {t(link.labelKey)}
                                </a>
                            ))}
                        </FooterLinkColumn>

                        <FooterLinkColumn title={t('solutions.title')}>
                            <a
                                title={t('solutions.aiAll')}
                                href={localizedNavigation.href('/ai-all')}
                                onClick={() => trackLegacyFooterClick('ai-all')}
                                {...landingButtonAttrs('footer.solution.ai_all', 'navigate', 'ai-all')}
                            >
                                {t('solutions.aiAll')}
                            </a>
                            {navigate(t('resources.templates'), '/marketplace/templates', 'templates')}
                            {navigate(t('resources.components'), '/marketplace/components', 'components')}
                        </FooterLinkColumn>

                        <FooterLinkColumn title={t('features.title')}>
                            <a
                                title={t('features.enterAprilLaunch')}
                                href="https://enter.converge.ai/blog/enter-april-launch"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('enter-april-launch')}
                                {...landingButtonAttrs(
                                    'footer.feature.enter_april_launch',
                                    'navigate',
                                    'enter-april-launch',
                                )}
                            >
                                {t('features.enterAprilLaunch')}
                            </a>
                            <a
                                title={t('features.enterCloud')}
                                href="https://enter.converge.ai/blog/enter-cloud"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('enter-cloud')}
                                {...landingButtonAttrs('footer.feature.enter_cloud', 'navigate', 'enter-cloud')}
                            >
                                {t('features.enterCloud')}
                            </a>
                            <a
                                title={t('features.enterSkills')}
                                href={EXTERNAL_LINKS.INTRODUCING_ENTER_SKILLS}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('enter-skills')}
                                {...landingButtonAttrs('footer.feature.enter_skills', 'navigate', 'enter-skills')}
                            >
                                {t('features.enterSkills')}
                            </a>
                            <a
                                title={t('features.aiAppBuilder')}
                                href="https://enter.converge.ai/features/ai-app-builder"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('ai-app-builder')}
                                {...landingButtonAttrs('footer.feature.ai_app_builder', 'navigate', 'ai-app-builder')}
                            >
                                {t('features.aiAppBuilder')}
                            </a>
                            <a
                                title={t('features.aiWebsiteBuilder')}
                                href="https://enter.converge.ai/features/ai-website-builder"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('ai-website-builder')}
                                {...landingButtonAttrs(
                                    'footer.feature.ai_website_builder',
                                    'navigate',
                                    'ai-website-builder',
                                )}
                            >
                                {t('features.aiWebsiteBuilder')}
                            </a>
                            <a
                                title={t('features.websiteTemplates')}
                                href="https://enter.converge.ai/features/website-template"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('website-template')}
                                {...landingButtonAttrs(
                                    'footer.feature.website_template',
                                    'navigate',
                                    'website-template',
                                )}
                            >
                                {t('features.websiteTemplates')}
                            </a>
                            <a
                                title={t('features.onlineCodeEditor')}
                                href="https://enter.converge.ai/features/code-editor"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('code-editor')}
                                {...landingButtonAttrs('footer.feature.code_editor', 'navigate', 'code-editor')}
                            >
                                {t('features.onlineCodeEditor')}
                            </a>
                            <a
                                title={t('features.aiAgentBuilder')}
                                href="https://enter.converge.ai/features/ai-agent-builder"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('ai-agent-builder')}
                                {...landingButtonAttrs(
                                    'footer.feature.ai_agent_builder',
                                    'navigate',
                                    'ai-agent-builder',
                                )}
                            >
                                {t('features.aiAgentBuilder')}
                            </a>
                            <a
                                title={t('features.visualEditor')}
                                href="https://enter.converge.ai/features/visual-editor"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('visual-editor')}
                                {...landingButtonAttrs('footer.feature.visual_editor', 'navigate', 'visual-editor')}
                            >
                                {t('features.visualEditor')}
                            </a>
                        </FooterLinkColumn>

                        <FooterLinkColumn title={t('resources.title')}>
                            <a
                                title={t('resources.pricing')}
                                href={pricingUrl}
                                onClick={handlePricing}
                                {...landingButtonAttrs('footer.resource.pricing', 'navigate', 'pricing')}
                            >
                                {t('resources.pricing')}
                            </a>
                            <a
                                title={t('resources.blog')}
                                href={EXTERNAL_LINKS.BLOG}
                                target="_blank"
                                rel="noopener noreferrer"
                                {...landingButtonAttrs('footer.resource.blog', 'navigate', 'blog')}
                            >
                                {t('resources.blog')}
                            </a>
                            <a
                                title={t('resources.changelog')}
                                href={EXTERNAL_LINKS.CHANGELOG}
                                target="_blank"
                                rel="noopener noreferrer"
                                {...landingButtonAttrs('footer.resource.changelog', 'navigate', 'changelog')}
                            >
                                {t('resources.changelog')}
                            </a>
                            <a
                                title={t('resources.enterCodeDocs')}
                                href={localizedNavigation.href('/docs/code')}
                                {...landingButtonAttrs('footer.resource.code_docs', 'navigate', 'code-docs')}
                            >
                                {t('resources.enterCodeDocs')}
                            </a>
                            <a
                                title={t('resources.forum')}
                                href={createTrackingLink(EXTERNAL_LINKS.FORUM, UTM_CONTENT.HOME)}
                                target="_blank"
                                rel="noopener noreferrer"
                                {...landingButtonAttrs('footer.resource.forum', 'navigate', 'forum')}
                            >
                                {t('resources.forum')}
                            </a>
                            <a
                                title={t('resources.features')}
                                href="https://enter.converge.ai/features"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('features')}
                                {...landingButtonAttrs('footer.resource.features', 'navigate', 'features')}
                            >
                                {t('resources.features')}
                            </a>
                            <a
                                title={t('resources.activity')}
                                href="https://enter.converge.ai/forum/activities"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('activity')}
                                {...landingButtonAttrs('footer.resource.activity', 'navigate', 'activity')}
                            >
                                {t('resources.activity')}
                            </a>
                            <a
                                title={t('resources.ambassador')}
                                href="https://enter.converge.ai/ambassador"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('ambassador')}
                                {...landingButtonAttrs('footer.resource.ambassador', 'navigate', 'ambassador')}
                            >
                                {t('resources.ambassador')}
                            </a>
                            <a
                                title={t('resources.school')}
                                href="https://enter.converge.ai/school"
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={() => trackLegacyFooterClick('school')}
                                {...landingButtonAttrs('footer.resource.school', 'navigate', 'school')}
                            >
                                {t('resources.school')}
                            </a>
                        </FooterLinkColumn>

                        <div className="link-footer__column-stack flex min-w-0 flex-col items-start gap-10 [@media(width<=900px)]:col-span-full [@media(width<=900px)]:grid [@media(width<=900px)]:grid-cols-2 [@media(width<=900px)]:gap-x-6 [@media(width<=900px)]:gap-y-12">
                            <FooterLinkColumn title={t('terms.title')}>
                                <a
                                    title={t('terms.termsOfService')}
                                    href={LEGAL_LINKS.termsOfService}
                                    onClick={() => trackLegacyFooterClick('terms')}
                                    {...landingButtonAttrs('footer.terms.terms_of_service', 'navigate', 'terms')}
                                >
                                    {t('terms.termsOfService')}
                                </a>
                                <a
                                    title={t('terms.privacyPolicy')}
                                    href={LEGAL_LINKS.privacyPolicy}
                                    onClick={() => trackLegacyFooterClick('privacy')}
                                    {...landingButtonAttrs('footer.terms.privacy_policy', 'navigate', 'privacy')}
                                >
                                    {t('terms.privacyPolicy')}
                                </a>
                                <button
                                    title={t('terms.cookieSettings')}
                                    type="button"
                                    onClick={() => {
                                        trackLegacyFooterClick('cookie_settings');
                                        openPreferenceCenter();
                                    }}
                                    {...landingButtonAttrs(
                                        'footer.terms.cookie_settings',
                                        'open_modal',
                                        'cookie-settings',
                                    )}
                                >
                                    {t('terms.cookieSettings')}
                                </button>
                            </FooterLinkColumn>
                            <FooterLinkColumn title={t('contact.title')}>
                                <a
                                    title={t('contact.feedback')}
                                    href="mailto:feedback@enter.pro"
                                    {...landingButtonAttrs('footer.contact.feedback', 'navigate', 'feedback-email')}
                                >
                                    {t('contact.feedback')}
                                </a>
                                <a
                                    title={t('contact.support')}
                                    href="mailto:support@enter.pro"
                                    {...landingButtonAttrs('footer.contact.support', 'navigate', 'support-email')}
                                >
                                    {t('contact.support')}
                                </a>
                            </FooterLinkColumn>
                        </div>
                    </div>
                </div>

                <div className="link-footer__bottom flex items-center justify-between gap-10 pt-8 [@media(width<=900px)]:flex-col [@media(width<=900px)]:items-start [@media(width<=900px)]:gap-7 [@media(width<=900px)]:border-t [@media(width<=900px)]:border-[#deded9]">
                    <div className="link-footer__legal flex items-center gap-[15px] text-[13px] font-medium leading-[19.5px] text-[#57534e] [&_p]:m-0 [@media(width<=900px)]:flex-col [@media(width<=900px)]:items-start">
                        <p>{t('copyright')}</p>
                        <LanguageSwitcher
                            className="link-footer__language ml-0 flex cursor-pointer items-center gap-1.5 border-0 bg-transparent p-0 text-sm font-medium leading-5 text-[#525252] [&_select]:text-[#525252]"
                            trackingButtonId={isLandingV2 ? 'footer.language' : undefined}
                            trackingButtonScreen={isLandingV2 ? 'footer' : undefined}
                            trackingAction={isLandingV2 ? 'select' : undefined}
                        />
                    </div>
                    <div className="link-footer__utilities flex items-center gap-6 pr-6 [@media(width<=900px)]:flex-col [@media(width<=900px)]:items-start [@media(width<=900px)]:pr-0">
                        <div className="link-footer__socials flex items-center gap-4 [&_a]:block [&_a]:size-[18px] [&_img]:block [&_img]:size-[18px]" aria-label={t('ariaLabels.socialMedia')}>
                            {[
                                [t('socialAriaLabels.x'), EXTERNAL_LINKS.X, 'footer-x.svg'],
                                [t('socialAriaLabels.discord'), EXTERNAL_LINKS.DISCORD, 'footer-discord.svg'],
                                [t('socialAriaLabels.tiktok'), EXTERNAL_LINKS.TIKTOK, 'footer-tiktok.svg'],
                                [t('socialAriaLabels.youtube'), EXTERNAL_LINKS.YOUTUBE, 'footer-youtube.svg'],
                                [t('socialAriaLabels.linkedin'), EXTERNAL_LINKS.LINKEDIN, 'footer-linkedin.svg'],
                            ].map(([label, href, asset]) => {
                                const socialId = asset.replace('footer-', '').replace('.svg', '');
                                return (
                                    <a
                                        href={href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        aria-label={label}
                                        key={href}
                                        {...landingButtonAttrs(`footer.social.${socialId}`, 'navigate', socialId)}
                                    >
                                        <img src={`/_enter_home/landing-v2/${asset}`} alt="" />
                                    </a>
                                );
                            })}
                        </div>
                        <button
                            className="link-footer__back-to-top flex cursor-pointer items-center gap-2 border-0 bg-transparent p-0 text-[13px] font-medium leading-[19.5px] text-[#525252] [&_svg]:size-4"
                            type="button"
                            onClick={() => {
                                window.scrollTo({ top: 0, behavior: 'smooth' });
                                trackLegacyFooterClick('back_to_top');
                            }}
                            {...landingButtonAttrs('footer.back_to_top', 'scroll', 'top')}
                        >
                            {t('backToTop')}
                            <ArrowUpCircle aria-hidden="true" />
                        </button>
                    </div>
                </div>
            </div>
        </footer>
    );
}
